import React from "react";
import { Box, Stack, Typography, Button, useTheme } from "@mui/material";
import { HiOutlineMinusSm, HiOutlinePlusSm } from "react-icons/hi";
import { useDispatch } from "react-redux";
import { useAppStore } from "@/global/store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import {
  gameFiltersState,
  selectDynamicSeatCount,
  selectSeatCount,
  selectMaxSeatCount,
} from "@/global/store/data/gameFilters";
import { useFilterPanelsState } from "../useFilterPanels";
import { filterPanelNames } from "@/global/config/constants";

interface DynamicSeatCountProps {
  onConfirm?: (seatCounts: { id: number; seatCount: number }[]) => void;
}

export function DynamicSeatCount({ onConfirm }: DynamicSeatCountProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const teamConfig = useTeamConfig();

  const currentSeatCounts = useAppStore(selectDynamicSeatCount);
  const totalSeatCount = useAppStore(selectSeatCount);
  const maxSeatCount = useAppStore(selectMaxSeatCount);
  const { set: setFilterPanelsState } = useFilterPanelsState();
  // Get configuration from team config
  const config =
    teamConfig?.uiConfigs?.gameFiltersSettings?.dynamicSeatCount || [];

  // Sort config by orderIndex
  const sortedConfig = [...config].sort((a, b) => a.orderIndex - b.orderIndex);

  const getSeatCount = (id: number): number => {
    const seatCount = currentSeatCounts.find((sc) => sc.id === id);
    return seatCount?.seatCount || 0;
  };

  const handleIncrement = (id: number, title: string) => {
    const currentCount = getSeatCount(id);
    const newCount = currentCount + 1;

    // Get the orderIndex from config
    const configItem = sortedConfig.find((item) => item.id === id);
    const orderIndex = configItem?.orderIndex || 0;

    dispatch(
      gameFiltersState.actions.setDynamicSeatCountItem({
        id,
        seatCount: newCount,
        orderIndex,
      })
    );

    // Call onConfirm if provided
    if (onConfirm) {
      const updatedCounts = currentSeatCounts.map((sc) =>
        sc.id === id ? { ...sc, seatCount: newCount } : sc
      );
      // Add new entry if it doesn't exist
      if (!currentSeatCounts.find((sc) => sc.id === id)) {
        updatedCounts.push({ id, seatCount: newCount, orderIndex });
      }
      onConfirm(updatedCounts);
    }

    setFilterPanelsState({
      lastChangedFilter: filterPanelNames.dynamic_seat_count,
    });
  };

  const handleDecrement = (id: number, title: string) => {
    const currentCount = getSeatCount(id);
    if (currentCount > 0) {
      const newCount = currentCount - 1;

      // Get the orderIndex from config
      const configItem = sortedConfig.find((item) => item.id === id);
      const orderIndex = configItem?.orderIndex || 0;

      dispatch(
        gameFiltersState.actions.setDynamicSeatCountItem({
          id,
          seatCount: newCount,
          orderIndex,
        })
      );

      // Call onConfirm if provided
      if (onConfirm) {
        const updatedCounts = currentSeatCounts.map((sc) =>
          sc.id === id ? { ...sc, seatCount: newCount } : sc
        );
        onConfirm(updatedCounts);
      }

      setFilterPanelsState({
        lastChangedFilter: filterPanelNames.dynamic_seat_count,
      });
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        {sortedConfig.map((option) => {
          const count = getSeatCount(option.id);
          const isDecrementDisabled = count === 0;

          // Check if incrementing would exceed or reach the max seat count
          // Disable when: maxSeatCount is set AND totalSeatCount >= maxSeatCount
          const isIncrementDisabled =
            maxSeatCount > 0 && totalSeatCount >= maxSeatCount;

          return (
            <Box key={option.id} role="group" aria-label={option.title}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Box flex={1}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color={theme.palette.text.primary}
                    sx={{ mb: 0.5 }}
                  >
                    {option.title}
                  </Typography>
                  {option.description && (
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                      sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>

                {/* Counter matching the existing Counter component design */}
                <Stack
                  direction="row"
                  borderRadius="6px"
                  overflow="hidden"
                  border={`1px solid ${theme.palette.divider}`}
                  sx={{ minWidth: "fit-content" }}
                >
                  <Button
                    variant="contained"
                    disabled={isDecrementDisabled}
                    onClick={() => handleDecrement(option.id, option.title)}
                    aria-label={`Remove ${option.title} seat`}
                    sx={{
                      fontSize: "1.2rem",
                      borderRadius: 0,
                      width: "48px",
                      minWidth: "48px",
                      height: "48px",
                      padding: "12px",
                      border: "none",
                      "&:not(:disabled)": {
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <HiOutlineMinusSm />
                  </Button>

                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      minWidth: "60px",
                      height: "48px",
                      backgroundColor: theme.palette.background.paper,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={theme.palette.text.primary}
                      sx={{ fontSize: "1.2rem" }}
                    >
                      {count}
                    </Typography>
                  </Stack>

                  <Button
                    variant="contained"
                    disabled={isIncrementDisabled}
                    onClick={() => handleIncrement(option.id, option.title)}
                    aria-label={`Add ${option.title} seat`}
                    sx={{
                      fontSize: "1.2rem",
                      borderRadius: 0,
                      width: "48px",
                      minWidth: "48px",
                      height: "48px",
                      padding: "12px",
                      border: "none",
                      "&:not(:disabled)": {
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <HiOutlinePlusSm />
                  </Button>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
